import { STORAGE, getLocalStorage, setLocalStorage } from '@/shared/lib/storage'
import { isHexColor } from '@/shared/ui/theme/tokens'
import type React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { LayoutSettingsContext } from './layoutSettingsStore'
import {
  DEFAULT_SPIN_ICON,
  type LayoutSettings,
  MAX_CUSTOM_COLORS,
  buildLayoutCssVars,
  defaultLayoutSettings,
  isSpinIcon,
} from './settings'

// Giữ đường import công khai `@/shared/ui/theme/LayoutSettingsContext`.
export { useLayoutSettings } from './layoutSettingsStore'

/**
 * Nguồn sự thật duy nhất cho bố cục đang chạy. Vai trò như state `settings` mà app
 * của Ant Design Pro giữ rồi truyền vào `<ProLayout {...settings}>` — `LayoutSettingDrawer`
 * chỉ ghi vào đây, `LayoutAdmin` đọc ra.
 *
 * Persist bằng localStorage thay vì URL query param như pro: app này không có nhu cầu
 * share link cấu hình, còn param thì bẩn mọi route và dễ va query nghiệp vụ.
 */
export const LayoutSettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const [settings, setSettings] = useState<LayoutSettings>(() => {
    // Merge với default: giá trị cũ trong máy dev thiếu field mới vẫn chạy được.
    const merged = {
      ...defaultLayoutSettings,
      ...(getLocalStorage<Partial<LayoutSettings>>(STORAGE.LAYOUT_SETTINGS) ?? {}),
    }
    // Màu tuỳ chọn là dữ liệu người dùng — chỉ nhận `#RRGGBB` hợp lệ.
    if (!isHexColor(merged.customPrimary)) merged.customPrimary = ''
    if (!isHexColor(merged.customPageBg)) merged.customPageBg = ''
    // Giá trị icon loading cũ / lạ trong localStorage thì rơi về mặc định.
    if (!isSpinIcon(merged.spinIcon)) merged.spinIcon = DEFAULT_SPIN_ICON
    merged.customColors = Array.isArray(merged.customColors)
      ? merged.customColors.filter(isHexColor).slice(0, MAX_CUSTOM_COLORS)
      : []
    // Tự động khôi phục splitMenus: false cho layout mix nếu bị lưu nhầm từ phiên trước (trừ môi trường test)
    if (process.env.NODE_ENV !== 'test' && merged.layout === 'mix' && merged.splitMenus) {
      merged.splitMenus = false
    }
    // Đảm bảo headerHeight tối thiểu 64px (theme MBseller) để tránh avatar/button bị cắt chân
    if (!merged.headerHeight || merged.headerHeight < 56) {
      merged.headerHeight = 64
    }
    // Đảm bảo siderWidth tối thiểu 260px để không cắt chữ menu
    if (!merged.siderWidth || merged.siderWidth < 260) {
      merged.siderWidth = 290
    }
    return merged
  })

  const setSetting = useCallback(
    <K extends keyof LayoutSettings>(key: K, value: LayoutSettings[K]) => {
      setSettings((prev) => ({ ...prev, [key]: value }))
    },
    []
  )

  const reset = useCallback(() => setSettings(defaultLayoutSettings), [])

  useEffect(() => {
    setLocalStorage(STORAGE.LAYOUT_SETTINGS, settings)

    const root = document.documentElement
    for (const [key, value] of Object.entries(buildLayoutCssVars(settings))) {
      root.style.setProperty(key, value)
    }

    // Copy đúng antd pro (`SettingDrawer`): weak mode = filter invert trên body.
    // Đây là quirk của pro chứ không phải mô phỏng mù màu chuẩn — giữ để khớp hành vi.
    document.body.style.filter = settings.colorWeak ? 'invert(80%)' : ''
  }, [settings])

  const value = useMemo(() => ({ settings, setSetting, reset }), [settings, setSetting, reset])

  return <LayoutSettingsContext.Provider value={value}>{children}</LayoutSettingsContext.Provider>
}
